import axios from "axios";
import Session from "supertokens-auth-react/recipe/session";

// Create Axios instance
const API_BASE_URL = process.env.REACT_APP_API_DOMAIN || (process.env.NODE_ENV === 'development'
  ? process.env.REACT_APP_LOCAL_API_DOMAIN
  : "https://api.yourshirt.co");

if (process.env.NODE_ENV === 'development') {
  console.log("API_BASE_URL being used by apiClient:", API_BASE_URL);
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,  // This enables cookie-based authentication!
  timeout: 180000, // Increase timeout to 180 seconds (3 minutes)
});

if (typeof Session.addAxiosInterceptors === "function") {
  Session.addAxiosInterceptors(api);
}


/** Normalize paginated list API: { items, total, limit, offset } or legacy bare array */
export const normalizePaginatedItems = (data) => {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Stable key for catalog rows (provider + id; supports multiple sources when enabled on the API).
 * Not shown in the UI; used to call /products/{id}/details?printing_provider=...
 */
export const catalogItemKey = (p) => {
  if (!p || p.id == null) return '';
  const prov = (p.catalog_provider || 'Printful').trim() || 'Printful';
  return `${prov}:${p.id}`;
};

export const parseCatalogItemKey = (key) => {
  if (key == null || key === '') return { id: null, printing_provider: 'Printful' };
  const s = String(key);
  const idx = s.indexOf(':');
  if (idx < 0) {
    const id = parseInt(s, 10);
    return { id: Number.isNaN(id) ? null : id, printing_provider: 'Printful' };
  }
  const printing_provider = s.slice(0, idx).trim() || 'Printful';
  const id = parseInt(s.slice(idx + 1), 10);
  return { printing_provider, id: Number.isNaN(id) ? null : id };
};

/** Page sizes for “Load more” (must stay within backend max limits). */
export const CATALOG_PAGE_SIZE = 48;
/** Backend caps catalog at PRODUCTS_CATALOG_MAX_LIMIT (default 500). */
export const CATALOG_MAX_LIMIT = 500;
export const GALLERY_PAGE_SIZE = 50;
export const MOCKUPS_PAGE_SIZE = 50;
/** Admin Design Assets table — keeps presigned image URLs per page small. */
export const ADMIN_DESIGNS_PAGE_SIZE = 10;
/** Storefront Ready-Made gallery default page size (public catalog). */
export const READY_MADE_PAGE_SIZE = 48;

export const getProductsCatalog = async (params = {}) => {
  const response = await api.get('/products-catalog', {
    params: { limit: CATALOG_PAGE_SIZE, offset: 0, discontinued: false, ...params },
  });
  return response.data;
};

export const getAdminViewerRegionDebug = async () => {
  const response = await api.get('/admin/debug/viewer-region');
  return response.data;
};

/** Distinct catalog filter options (product types, shipping, print placements); Redis-cached on backend. */
export const getCatalogFacets = async (params = {}) => {
  const merged = { discontinued: false, ...params };
  const response = await api.get('/products-catalog/facets', {
    params: Object.fromEntries(Object.entries(merged).filter(([, v]) => v != null && v !== '')),
  });
  return response.data;
};

export const getUserGallery = async (params = {}) => {
  const response = await api.get('/gallery', {
    params: { limit: GALLERY_PAGE_SIZE, offset: 0, ...params },
  });
  return response.data;
};

/** Paginated Ready-Made rows for the logged-in admin (edit/delete). */
export const getAdminReadyMadeAssets = async (params = {}) => {
  const response = await api.get('/gallery', {
    params: {
      ready_made_only: true,
      limit: ADMIN_DESIGNS_PAGE_SIZE,
      offset: 0,
      ...params,
    },
  });
  return response.data;
};

export const getUserMockups = async (params = {}) => {
  const response = await api.get('/mockups', {
    params: { limit: MOCKUPS_PAGE_SIZE, offset: 0, ...params },
  });
  return response.data;
};

export const uploadImage = async (file, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (options.mockupFile) {
    formData.append('mockup_file', options.mockupFile);
  }
  if (options.description != null && options.description !== '') {
    formData.append('description', options.description);
  }
  const url = options.readyMade ? '/upload-image?ready_made=true' : '/upload-image';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/** Persist product-first flow so /gallery can restore image if route state is partial. */
export const FLOW_SELECTED_PRODUCT_STORAGE_KEY = 'tshirt_flow_selected_product_v1';

export function persistFlowSelectedProduct(product) {
  if (typeof sessionStorage === 'undefined' || !product) return;
  try {
    sessionStorage.setItem(
      FLOW_SELECTED_PRODUCT_STORAGE_KEY,
      JSON.stringify({
        selectedProduct: product,
        heroImage: product?.image || null,
        catalogItemKey: catalogItemKey(product),
        at: Date.now(),
      }),
    );
  } catch (_) {
    /* quota / private mode */
  }
}

export function readFlowSelectedProduct() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(FLOW_SELECTED_PRODUCT_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o?.selectedProduct) return null;
    if (o.at && Date.now() - o.at > 86400000 * 2) {
      sessionStorage.removeItem(FLOW_SELECTED_PRODUCT_STORAGE_KEY);
      return null;
    }
    return o;
  } catch {
    return null;
  }
}

export function clearFlowSelectedProduct() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(FLOW_SELECTED_PRODUCT_STORAGE_KEY);
  } catch (_) {}
}

export const GUEST_EMAIL_STORAGE_KEY = 'yourshirt_guest_email';

export function persistGuestEmail(email) {
  if (typeof sessionStorage === 'undefined') return;
  const value = String(email || '').trim().toLowerCase();
  if (!value) return;
  try {
    sessionStorage.setItem(GUEST_EMAIL_STORAGE_KEY, value);
  } catch (_) {}
}

export function readGuestEmail() {
  if (typeof sessionStorage === 'undefined') return '';
  try {
    return sessionStorage.getItem(GUEST_EMAIL_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function guestEmailParams(extra = {}) {
  const email = readGuestEmail();
  return email ? { ...extra, email } : { ...extra };
}

/** printing_provider query for GET /products/:id/details */
export function catalogProductPrintingProvider(product) {
  if (!product) return 'Printful';
  const raw = String(product.catalog_provider || '').trim();
  if (/printify/i.test(raw)) return 'Printify';
  if (/printful/i.test(raw)) return 'Printful';
  if (product.provider_mappings && typeof product.provider_mappings === 'object') {
    if (product.provider_mappings.Printify) return 'Printify';
    if (product.provider_mappings.Printful) return 'Printful';
  }
  return 'Printful';
}

/** Best image URL from product details payload (primary image or files mockup/preview). */
export function productDetailsHeroImage(details) {
  if (!details) return '';
  const primary = details.image;
  if (primary && String(primary).trim()) return String(primary).trim();
  const files = details.files;
  const items = Array.isArray(files)
    ? files.filter((f) => f && typeof f === 'object')
    : files && typeof files === 'object'
      ? Object.values(files).filter((f) => f && typeof f === 'object')
      : [];
  for (const f of items) {
    const t = String(f.type || f.id || '').toLowerCase();
    if (t === 'mockup' || t === 'preview') {
      const u = f.url || f.image_url || f.preview_url || f.thumbnail || f.src;
      if (u && String(u).trim()) return String(u).trim();
    }
  }
  for (const f of items) {
    const u = f.url || f.image_url || f.preview_url || f.thumbnail || f.src;
    if (u && String(u).trim()) return String(u).trim();
  }
  return '';
}

/** Paginated ready-made payload (items, total, limit, offset). */
export const getReadyMadeDesignsPage = async (params = {}) => {
  try {
    const merged = { limit: READY_MADE_PAGE_SIZE, offset: 0, ...params };
    const query = Object.fromEntries(
      Object.entries(merged).filter(([, v]) => v != null && v !== ''),
    );
    const response = await api.get('/designs/ready-made', {
      params: query,
    });
    const data = response.data;
    if (data != null && Array.isArray(data.items)) {
      return data;
    }
    const items = normalizePaginatedItems(data);
    return {
      items,
      total: items.length,
      limit: params.limit ?? READY_MADE_PAGE_SIZE,
      offset: params.offset ?? 0,
    };
  } catch (error) {
    console.error('Error fetching ready-made designs:', error);
    return { items: [], total: 0, limit: READY_MADE_PAGE_SIZE, offset: 0 };
  }
};

/** Flat list for gallery/landing (handles paginated API and legacy array responses). */
export const getReadyMadeDesigns = async (params = {}) => {
  const page = await getReadyMadeDesignsPage(params);
  return page.items ?? [];
};

/** Gallery card: prefer mockup preview; falls back to print artwork. */
export const readyMadeDisplayUrl = (design) =>
  design?.mockup_image_url || design?.image_url || '';

/**
 * Pass this object as route state `selectedImage` when continuing to the product catalog.
 * `image_url` is always print-ready; `mockup_image_url` is optional for banners/previews.
 */
export const readyMadeToFlowSelectedImage = (design) => {
  if (!design) return null;
  return {
    id: design.id,
    image_url: design.image_url,
    prompt: design.name,
    style: design.style,
    mockup_image_url: design.mockup_image_url ?? null,
  };
};

export const createMockupTask = async (mockupData) => {
  const response = await api.post('/create-mockup', mockupData, {
    validateStatus: (status) => {
      return (status >= 200 && status < 300) || status === 202;
    },
  });
  return response;
};

export const getMockup = async (orderId) => {
  const response = await api.get(`/get-mockup/order/${orderId}`);
  return response.data;
};

export const deleteImage = async (imageId) => {
  const response = await api.delete(`/images/${imageId}`);
  return response.data;
};

/**
 * Replace Ready-Made print file and/or mockup in MinIO (admin). Omit options you are not changing.
 * @param {number} imageId
 * @param {{ printFile?: File; mockupFile?: File; clearMockup?: boolean; description?: string }} options
 */
export const replaceReadyMadeAssets = async (imageId, options = {}) => {
  const formData = new FormData();
  if (options.printFile) formData.append('file', options.printFile);
  if (options.mockupFile) formData.append('mockup_file', options.mockupFile);
  if (options.clearMockup) formData.append('clear_mockup', 'true');
  if (options.description != null && options.description !== '') {
    formData.append('description', options.description);
  }
  const response = await api.put(`/images/${imageId}/ready-made-assets`, formData);
  return response.data;
};

export const getProductDetails = async (productId, extraParams = {}) => {
  const response = await api.get(`/products/${productId}/details`, {
    params: { ...extraParams },
  });
  return response.data;
};

export const confirmPrintfulOrder = async (orderId) => {
  const response = await api.post(`/confirm-order/${orderId}`, null, {
    params: guestEmailParams(),
  });
  return response.data;
};

export const getPrintfulOrderStatus = async (orderId) => {
  const response = await api.get(`/orders/${orderId}/status`, {
    params: guestEmailParams(),
  });
  return response.data;
};

export const getOrders = async () => {
  const response = await api.get('/orders');
  return response.data;
};

export const getSingleOrder = async (orderId) => {
  const response = await api.get(`/orders/${orderId}`, {
    params: guestEmailParams(),
  });
  return response.data;
};



export const removeBackgroundImage = async (imageId) => {
  const response = await api.post(`/images/${imageId}/remove-background`);
  return response.data;
};

export const createPaymentIntent = async (paymentData) => {
  if (process.env.NODE_ENV === 'development') {
    console.log("Sending payment intent data to backend:", paymentData);
  }
  const response = await api.post('/create-payment-intent', paymentData);
  if (process.env.NODE_ENV === 'development') {
    console.log("Received payment intent response from backend:", response.data);
  }
  return response.data;
};

export const getPublicGallery = async (params = {}) => {
  try {
    const response = await api.get('/gallery/public', {
      params: { limit: GALLERY_PAGE_SIZE, offset: 0, ...params },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching public gallery:", error);
    return { items: [], total: 0, limit: 0, offset: 0 };
  }
};

export const getSavedAddresses = async () => {
  const response = await api.get('/users/me/addresses');
  return response.data;
};

export const saveAddress = async (addressData) => {
  const response = await api.post('/users/me/addresses', addressData);
  return response.data;
};

export const deleteSavedAddress = async (index) => {
  await api.delete(`/users/me/addresses/${index}`);
};

export const updateSavedAddress = async (index, addressData) => {
  const response = await api.put(`/users/me/addresses/${index}`, addressData);
  return response.data;
};

export const getProfile = async () => {
  const response = await api.get('/users/me/profile');
  return response.data;
};

export const updateProfile = async (profileData) => {
  const response = await api.put('/users/me/profile', profileData);
  return response.data;
};

export const uploadAvatar = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post("/users/me/avatar", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};
